/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Button, Collapse, Tooltip } from '@blueprintjs/core';
import { ColorAliases } from 'styles/variables';
import { SetOfTotal } from '../setOfTotal';
import { Sizes } from 'styles/variables-slykit';
import {
	alignedMinimalButton,
	hoverRevealChildrenDisplay,
	hoverRevealClassName,
	interactiveListItem,
	overflowEllipsis,
} from 'styles/styledComponentUtils';
import { facetPreviewCount } from '../../views/filterHub/facetVars';

/**
 * This component displays a smaller set of Facets that appears with the other property filters
 *
 * @param {DILProperty} property property to render
 * @param {function} openNewPanel passed down from blueprint PanelStack component. Opens a detailed facet component
 * @param {function} toggleCollapse callback to open/collapse the component
 * @param {boolean} isOpen is the component open or is it collapsed
 * @param {boolean} loading = false,
 * @param {number} activeFacetCount = 0,
 * @param {number} totalFacetCount = 0,
 * @param {number} totalRootFacetCount = totalFacetCount,
 * @param {function} onResetClick callback when the reset button is clicked
 */
export const FacetGroup = ({
	property,
	openNewPanel,
	toggleCollapse,
	isOpen = false,
	loading = false,
	activeFacetCount = 0,
	totalFacetCount = 0,
	totalRootFacetCount = totalFacetCount,
	onResetClick,
	children,
	...props
}) => {
	// STATELESS //

	const handleResetClick = (e) => {
		e.stopPropagation();
		onResetClick();
	};
	const handleOpenNewPanelClick = (e) => {
		e.stopPropagation();
		openNewPanel(property);
	};

	const cypressFacetOpenState = isOpen ? 'facet-expanded' : 'facet-collapsed';

	return (
		<div {...props} cy-test="facet-group" css={[panelContainer]}>
			<div
				css={[propertyHeader, hoverRevealChildrenDisplay, interactiveListItem]}
				onClick={() => toggleCollapse(property.name)}
			>
				<Tooltip hoverOpenDelay={2000} content={isOpen ? 'Collapse' : 'Open'}>
					<Button
						// onClick={() => toggleCollapse(property.name)} // parent does this
						cy-test={cypressFacetOpenState}
						rightIcon={isOpen ? 'chevron-down' : 'chevron-right'}
						minimal
						small
						css={css`
							margin-left: -4px;
						`}
					/>
				</Tooltip>

				<div css={title}>{property.name}</div>

				<SetOfTotal set={activeFacetCount} total={totalFacetCount} />

				<Tooltip hoverOpenDelay={2000} content={loading ? null : 'Reset'} className={hoverRevealClassName}>
					<Button
						loading={loading}
						onClick={handleResetClick}
						outlined // minimal
						small
						rightIcon={'reset'}
						css={css`
							margin-left: ${Sizes.xsm};
						`}
					/>
				</Tooltip>

				<Button // should this be here?
					cy-test="hover-reveal-open-panel"
					onClick={handleOpenNewPanelClick}
					// text={'All'} // too wide with text
					rightIcon={'arrow-right'}
					className={hoverRevealClassName}
					intent={'primary'}
					outlined // minimal
					small
					css={css`
						margin-left: ${Sizes.xsm};
					`}
				/>
			</div>

			<Collapse isOpen={isOpen}>
				{children}

				<div css={bottomRow}>
					{facetPreviewCount < totalRootFacetCount && (
						<Button // should this be here?
							cy-test="view-all"  
							onClick={handleOpenNewPanelClick}
							text={'View All'}
							rightIcon={'arrow-right'}
							intent={'primary'}
							css={[alignedMinimalButton]}
							minimal
							small
						/>
					)}
				</div>
			</Collapse>
		</div>
	);
};

const title = css`
	${overflowEllipsis}
	flex: 1 1 auto;
	margin: 0 ${Sizes.xxsm};
	font-weight: bold;
`;

const panelContainer = css`
	border-bottom: 1px solid ${ColorAliases.PT_DIVIDER_BLACK};
`;

const propertyHeader = css`
	width: 100%;
	padding: ${Sizes.sm} ${Sizes.lg};
	display: flex;
	align-items: center;
`;

const bottomRow = css`
	margin: ${Sizes.xsm} ${Sizes.lg} ${Sizes.md};
`;
